import { ListBulletIcon } from "@heroicons/react/24/solid";
import L from "leaflet";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  CircleMarker,
  MapContainer,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";

import BottomSheet from "../components/BottomSheet";
import LoadingSpinner from "../components/LoadingSpinner";
import MapPopup from "../components/map/MapPopup";
import SignalDot from "../components/SignalDot";
import StateBadge from "../components/StateBadge";
import { useAuth } from "../contexts/AuthContext";
import { useBoxes, type UseBoxesResult } from "../hooks/useBoxes";
import { STACKED_HEX, STATE_HEX, STATE_TW } from "../lib/colors";
import { fmtAgo, fmtCustomerWc, fmtDuration, mapDisplayCoords } from "../lib/format";
import { deriveSignalDots } from "../lib/signalDots";
import type { Box, State } from "../types/boxes";

// Default Leaflet icon paths break under CRA's bundler — we don't actually
// use the default markers (CircleMarker has no icon), but the global side
// effect from `leaflet` runs on import. Touching this is a no-op safe-guard.
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;

/**
 * Imperative bridge between sidebar selection and the Leaflet map. Lives
 * inside MapContainer so it can call useMap(). When the user clicks a pin
 * OR a sidebar entry, we panTo the box and openPopup on its marker.
 *
 * Pan-only (Jason 2026-05-05): we deliberately don't zoom in on click —
 * the operator already chose the zoom level they want; jumping to a
 * fixed zoom is disorienting. panTo preserves zoom and just recenters.
 *
 * Nonce-bumped (Jason 2026-05-06 bug): clicking the SAME box twice (or
 * clicking, applying a filter, then re-clicking) needs to re-fire the
 * effect even though the box reference is unchanged. We bump a numeric
 * nonce on every selection event so the deps array always changes.
 */
interface SelectionState {
  box: Box | null;
  nonce: number;
}

const MapController: React.FC<{
  selection: SelectionState;
  markerRefs: React.MutableRefObject<Map<string, L.CircleMarker | null>>;
  fleetBounds: L.LatLngBounds | null;
}> = ({ selection, markerRefs, fleetBounds }) => {
  const map = useMap();
  // Frame the actual fleet, ONCE, when coordinates first arrive.
  //
  // MapContainer's `center`/`zoom` are mount-only props in react-leaflet:
  // on the first render `boxesWithCoords` is still empty, so the map mounts
  // on the [39.5,-98.35] US fallback at a hard-coded zoom and then never
  // moves when the snapshot lands. That framing dated from when the fleet
  // was gulf/southern-US only; it now spans lat 27.7 (south TX) to 48.9
  // (North Dakota), so the fixed zoom shows a continent of empty map.
  //
  // Fit once and only once: re-fitting on every poll would yank the view
  // out from under an operator who has zoomed in, which is the same reason
  // click-to-select is pan-only (see the file header).
  const hasFitted = useRef(false);
  useEffect(() => {
    if (hasFitted.current || !fleetBounds) return;
    hasFitted.current = true;
    // Re-measure first — fitBounds derives zoom from the container size,
    // and inside the Dashboard that size is still settling at this point.
    map.invalidateSize();
    map.fitBounds(fleetBounds, {
      padding: [40, 40],
      // Don't slam to street level if the fleet ever collapses to one box.
      maxZoom: 9,
    });
  }, [map, fleetBounds]);
  // Leaflet measures the container ONCE at mount. When we embed the map
  // inside the Dashboard the parent's height keeps shifting as the KPI +
  // Fleet-health rows load in, and the initial "100%" resolves to something
  // small -- the tiled area renders at that stale size and the newly-grown
  // container shows grey bands above/below the actual map. invalidateSize()
  // re-reads the current container box and re-renders tiles; we call it on
  // mount and again 250ms later to catch the second layout pass. A
  // ResizeObserver keeps it correct if the browser is resized later.
  useEffect(() => {
    map.invalidateSize();
    const t = setTimeout(() => map.invalidateSize(), 250);
    const container = map.getContainer();
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => map.invalidateSize())
        : null;
    if (ro && container) ro.observe(container);
    return () => {
      clearTimeout(t);
      ro?.disconnect();
    };
  }, [map]);
  // The mount + 250ms pair above catches the initial layout passes, but the
  // Dashboard embed keeps growing as the KPI/donut rows resolve, which can
  // land after both. Re-measuring when the fleet bounds first resolve covers
  // that later settle — a stale measurement is what leaves grey bands above
  // and below the tiles.
  useEffect(() => {
    if (!fleetBounds) return;
    map.invalidateSize();
  }, [map, fleetBounds]);
  useEffect(() => {
    const b = selection.box;
    if (!b || b.lat == null || b.lon == null) return;
    map.panTo(mapDisplayCoords(b.lat, b.lon), { animate: true, duration: 0.4 });
    const marker = markerRefs.current.get(b.scu_name);
    marker?.openPopup();
  }, [selection.nonce, selection.box, map, markerRefs]);
  return null;
};

/**
 * Presentational fleet map. Takes an already-fetched boxes result as props so
 * an embedding page (the Dashboard) can share ITS single /api/boxes fetch
 * instead of triggering a second one. `MapPage` below is the standalone
 * wrapper that fetches on its own /map route.
 */
export const FleetMap: React.FC<UseBoxesResult> = ({
  snapshot,
  loading,
  error,
  lastFetchedAt,
  refresh,
}) => {
  const { can } = useAuth();
  // Operator detail (signal dots, version, peplink roster, stacked toggle) is
  // gated on the operator.triage capability — held only by the platform roles,
  // so it stays platform-only while being capability-driven. Customer/vendor
  // maps show identity + position + coarse online state only.
  const operatorView = can("operator.triage");
  const [selection, setSelection] = useState<SelectionState>({
    box: null,
    nonce: 0,
  });
  const selected = selection.box;
  const selectBox = (b: Box): void =>
    setSelection((prev) => ({ box: b, nonce: prev.nonce + 1 }));
  // Click a state-count chip to filter the pins. Click again to clear.
  // Mirrors the BoxesPage chip behaviour.
  const [stateFilter, setStateFilter] = useState<State | null>(null);
  // Push K Slice 5: separate filter chip for STACKED. Distinct from
  // stateFilter because a stacked box can be in any state — operators
  // want to see "all stacked" regardless of the underlying signals.
  const [stackedFilter, setStackedFilter] = useState<boolean>(false);
  // Free-text search across SCU name / smartbox code / workcenter /
  // customer / location. Case-insensitive substring match.
  const [searchQ, setSearchQ] = useState<string>("");
  // Push K Slice 2: mobile-only bottom-sheet toggle for the Active
  // Issues list. Desktop (lg:+) shows the persistent right sidebar
  // instead and ignores this state.
  const [sheetOpen, setSheetOpen] = useState(false);
  // scu_name → CircleMarker ref, populated as pins render. MapController
  // reads this to imperatively openPopup() when the sidebar selection changes.
  const markerRefs = useRef<Map<string, L.CircleMarker | null>>(new Map());

  // Substring match across the box's user-visible identifiers. Empty
  // query matches everything. Computed once per searchQ change.
  const searchMatch = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return () => true;
    return (b: Box): boolean => {
      const fields = [
        b.scu_name,
        b.smartbox_code,
        b.work_center,
        b.customer,
        b.location,
      ];
      return fields.some(
        (f) => typeof f === "string" && f.toLowerCase().includes(q),
      );
    };
  }, [searchQ]);

  const boxesWithCoords = useMemo(
    () =>
      (snapshot?.boxes ?? [])
        .filter((b) => b.lat != null && b.lon != null)
        .filter((b) => !stateFilter || b.state === stateFilter)
        .filter((b) => !stackedFilter || b.is_stacked)
        .filter(searchMatch),
    [snapshot, stateFilter, stackedFilter, searchMatch],
  );

  const stackedCount = useMemo(
    () => (snapshot?.boxes ?? []).filter((b) => b.is_stacked).length,
    [snapshot],
  );

  // Sidebar / bottom-sheet content — actively-malfunctioning boxes
  // (DOWN + SCANNER_ONLY + CAMERA_ONLY). POWERED_DOWN excluded so
  // routine power cycles don't crowd out actual issues. Sorted DOWN
  // first, then by elapsed-time desc.
  const sidebarBoxes = useMemo(
    () =>
      (snapshot?.boxes ?? [])
        .filter(
          (b) =>
            !b.is_stacked &&
            (b.state === "DOWN" ||
              b.state === "SCANNER_ONLY" ||
              b.state === "CAMERA_ONLY"),
        )
        .sort((a, b) => {
          const rank = (s: typeof a.state) => (s === "DOWN" ? 0 : 1);
          return (
            rank(a.state) - rank(b.state) ||
            (b.elapsed_seconds ?? 0) - (a.elapsed_seconds ?? 0)
          );
        }),
    [snapshot],
  );

  const sidebarTitle = "Active Issues";

  // Stable per-marker render props (Jason 2026-06-15). The 30s
  // `useBoxes` poll creates a fresh boxes array every cycle even when
  // no box's data has changed — that forces every downstream useMemo
  // to recompute with new object identities, so react-leaflet receives
  // new pathOptions+center objects per marker and redraws them all. If
  // your pan happens to overlap a poll, the brief redraw window looks
  // like markers disappearing.
  //
  // Fix: cache the rendered props per scu_name. A content signature
  // captures the only fields that actually affect rendering (state,
  // is_stacked, is_default_location, lat, lon). Two polls returning
  // the same data → same signature → reuse the cached object → same
  // identity → react-leaflet sees no change → no redraw.
  const propsCache = useRef<
    Map<string, { center: [number, number]; pathOptions: L.PathOptions; signature: string }>
  >(new Map());
  const markerProps = useMemo(
    () => boxesWithCoords.map((b) => {
      const dotColor = b.is_stacked ? STACKED_HEX : STATE_HEX[b.state];
      const sig = `${b.state}|${b.is_stacked ? 1 : 0}|${b.is_default_location ? 1 : 0}|${b.lat}|${b.lon}`;
      const cached = propsCache.current.get(b.scu_name);
      if (cached && cached.signature === sig) {
        return { box: b, center: cached.center, pathOptions: cached.pathOptions };
      }
      const next = {
        center: mapDisplayCoords(b.lat as number, b.lon as number),
        pathOptions: {
          color: dotColor,
          fillColor: dotColor,
          fillOpacity: b.is_default_location && !b.is_stacked ? 0.3 : 0.7,
          weight: 2,
          dashArray: b.is_default_location ? "3 3" : undefined,
        } as L.PathOptions,
        signature: sig,
      };
      propsCache.current.set(b.scu_name, next);
      return { box: b, center: next.center, pathOptions: next.pathOptions };
    }),
    [boxesWithCoords],
  );

  // Centre on the centroid of GPS-tagged boxes; fall back to mid-US.
  // Use display coords (NYC remapped to Houston) so a stray bogus NYC fix
  // doesn't yank the centroid east.
  // Bounding box of every box that actually has GPS — what the map frames
  // on first load. null until coordinates arrive, which is the signal
  // MapController waits for.
  const fleetBounds = useMemo<L.LatLngBounds | null>(() => {
    if (boxesWithCoords.length === 0) return null;
    return L.latLngBounds(
      boxesWithCoords.map((b) =>
        mapDisplayCoords(b.lat as number, b.lon as number),
      ),
    );
  }, [boxesWithCoords]);

  const centre = useMemo<[number, number]>(() => {
    if (boxesWithCoords.length === 0) return [39.5, -98.35];
    let latSum = 0;
    let lonSum = 0;
    for (const b of boxesWithCoords) {
      const [dlat, dlon] = mapDisplayCoords(b.lat as number, b.lon as number);
      latSum += dlat;
      lonSum += dlon;
    }
    return [latSum / boxesWithCoords.length, lonSum / boxesWithCoords.length];
  }, [boxesWithCoords]);

  return (
    <div className="h-full flex flex-col">
      <header className="px-4 md:px-6 py-3 md:py-4 border-b border-secondary-200 bg-white shadow-sm">
        {/* Row 1 — title + meta on the left, refresh on the right.
            Title gets the full left half so the meta line isn't
            squashed by the chip strip. (No sticky behaviour any more —
            the map now sits inside the Dashboard page, so a sticky
            header would float over unrelated content above.) */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold">Fleet Map</h1>
            <div className="text-xs text-secondary-500 mt-0.5">
              {snapshot ? (
                <>
                  {snapshot.total_boxes} boxes · {boxesWithCoords.length} with GPS ·
                  generated{" "}
                  {new Date(snapshot.generated_utc).toLocaleString()}
                  {lastFetchedAt && (
                    <>
                      {" "}
                      · last fetched {lastFetchedAt.toLocaleTimeString()}
                    </>
                  )}
                </>
              ) : loading ? (
                "loading…"
              ) : (
                "no data"
              )}
            </div>
          </div>
          {/* Free-text search — matches against SCU name, smartbox
              code, workcenter, customer, and location. Live filter
              (no submit). The Refresh button moved up to the Dashboard
              header (DashboardPage) since the map embeds inside it;
              the map's own snapshot auto-polls per pollIntervalSeconds
              and catches up without a per-page button. */}
          <div className="shrink-0">
            <input
              type="search"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search SB### / workcenter / customer"
              className="w-44 sm:w-56 px-2 py-1 text-xs rounded border border-secondary-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
        </div>

        {/* Row 2 — filter chips. flex-wrap lets them spill to a second
            line on narrow screens instead of horizontal-scrolling. */}
        {snapshot && (
          <div className="flex flex-wrap gap-1.5 mt-2 text-xs">
            {(
              [
                "UP",
                "SCANNER_ONLY",
                "CAMERA_ONLY",
                "UNKNOWN",
                "DOWN",
                "POWERED_DOWN",
                "UNTOUCHED",
              ] as State[]
            ).map((s) => {
              const active = stateFilter === s;
              const count = snapshot.state_counts?.[s] ?? 0;
              const label =
                s === "SCANNER_ONLY"
                  ? "Scanner-only"
                  : s === "CAMERA_ONLY"
                    ? "Camera-only"
                    : s === "POWERED_DOWN"
                      ? "Powered-down"
                      : s.charAt(0) + s.slice(1).toLowerCase();
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() =>
                    setStateFilter((prev) => (prev === s ? null : s))
                  }
                  className={
                    "px-2 py-0.5 rounded-full border transition-colors " +
                    (active
                      ? "ring-2 ring-primary-400 " + STATE_TW[s]
                      : STATE_TW[s] + " hover:brightness-95")
                  }
                  title={`Click to ${active ? "clear" : "show only"} ${s} boxes`}
                >
                  {label} <strong>{count}</strong>
                </button>
              );
            })}
            {stackedCount > 0 && (
              <button
                type="button"
                onClick={() => setStackedFilter((prev) => !prev)}
                className={
                  "px-2 py-0.5 rounded-full border transition-colors bg-secondary-200 text-secondary-800 border-secondary-400 " +
                  (stackedFilter
                    ? "ring-2 ring-primary-400"
                    : "hover:bg-secondary-300")
                }
                title={`Click to ${stackedFilter ? "clear" : "show only"} STACKED boxes (intentionally offline)`}
              >
                Stacked <strong>{stackedCount}</strong>
              </button>
            )}
          </div>
        )}

        {error && (
          <div className="mt-2 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
            {error.message}
          </div>
        )}
      </header>

      {/* min-h-0: bound the map+sidebar row to its flex share so the Leaflet
          area (height:100%) stays a sane, top-aligned size and the Active
          Issues aside (overflow-auto) SCROLLS instead of stretching the row
          to the full list length (which was zooming the map out pole-to-pole). */}
      <div className="flex-1 flex min-h-0">
        <div className="flex-1 relative">
          {loading && !snapshot && (
            <div className="absolute inset-0 flex items-center justify-center z-[400]">
              <LoadingSpinner label="loading boxes" />
            </div>
          )}
          <MapContainer
            center={centre}
            // Default zoom: 5 on desktop frames the gulf/southern-US
            // deployment area nicely. On mobile the viewport is narrower,
            // so the same zoom crops half the fleet out — bump to 3 so
            // operators see most of North America when they first open
            // it on a phone. Width check runs once at mount; if the user
            // rotates the device they can pinch to taste.
            zoom={
              typeof window !== "undefined" && window.innerWidth < 768 ? 3 : 5
            }
            scrollWheelZoom
            style={{ height: "100%", width: "100%" }}
          >
            <MapController
              selection={selection}
              markerRefs={markerRefs}
              fleetBounds={fleetBounds}
            />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {markerProps.map(({ box: b, center, pathOptions }) => (
              <CircleMarker
                key={b.scu_name}
                ref={(el) => {
                  markerRefs.current.set(b.scu_name, el);
                }}
                center={center}
                radius={9}
                pathOptions={pathOptions}
                eventHandlers={{ click: () => selectBox(b) }}
              >
                <Popup maxWidth={300}>
                  <MapPopup box={b} operatorView={operatorView} onRefresh={() => void refresh()} />
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>

        {/* Desktop right sidebar — hidden below lg: where the BottomSheet
            takes over. Gated on operator.triage so customers/vendors don't
            see the Active Issues list at all. */}
        {operatorView && (
          <aside className="hidden lg:block w-80 border-l border-secondary-200 bg-white overflow-auto">
            <div className="p-4 border-b border-secondary-200">
              <h2 className="font-semibold text-sm uppercase tracking-wide text-secondary-700">
                {sidebarTitle} ({sidebarBoxes.length})
              </h2>
            </div>
            <ActiveIssuesList
              boxes={sidebarBoxes}
              selectedScu={selected?.scu_name ?? null}
              onSelect={selectBox}
            />
          </aside>
        )}
      </div>

      {/* Mobile-only floating button + BottomSheet — same operator gate. */}
      {operatorView && (
        <>
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            aria-label={`Open ${sidebarTitle.toLowerCase()} list (${sidebarBoxes.length} items)`}
            className="lg:hidden fixed bottom-4 right-4 z-[1080] rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-lg flex items-center gap-2 px-4 py-3 min-h-[48px] min-w-[48px]"
          >
            <ListBulletIcon className="h-5 w-5" />
            <span className="font-semibold text-sm">
              {sidebarBoxes.length === 0
                ? `No ${sidebarTitle.toLowerCase()}`
                : `${sidebarBoxes.length} issues`}
            </span>
          </button>

          <BottomSheet
            open={sheetOpen}
            onClose={() => setSheetOpen(false)}
            title={`${sidebarTitle} (${sidebarBoxes.length})`}
          >
            <ActiveIssuesList
              boxes={sidebarBoxes}
              selectedScu={selected?.scu_name ?? null}
              onSelect={(b) => {
                selectBox(b);
                setSheetOpen(false);
              }}
            />
          </BottomSheet>
        </>
      )}
    </div>
  );
};

/**
 * Standalone map route (`/map`). Fetches its own snapshot; the Dashboard
 * embeds <FleetMap> directly with its already-fetched scoped result instead,
 * so /api/boxes is fetched once there.
 */
export const MapPage: React.FC = () => {
  const result = useBoxes();
  return <FleetMap {...result} />;
};

/**
 * Shared list rendering for the desktop right-sidebar AND the mobile
 * BottomSheet. Same markup either way — only the chrome around it
 * differs. Push K Slice 2 (Jason 2026-05-06).
 */
const ActiveIssuesList: React.FC<{
  boxes: Box[];
  selectedScu: string | null;
  onSelect: (b: Box) => void;
}> = ({ boxes, selectedScu, onSelect }) => {
  const { can } = useAuth();
  const operatorView = can("operator.triage");
  if (boxes.length === 0) {
    return (
      <div className="p-4 text-sm text-secondary-500">No active issues. ✓</div>
    );
  }
  return (
    <ul>
      {boxes.map((b) => {
        const dots = deriveSignalDots(b);
        return (
          <li
            key={b.scu_name}
            className={
              "px-4 py-3 border-b border-secondary-100 cursor-pointer hover:bg-secondary-50 active:bg-secondary-100 " +
              (selectedScu === b.scu_name ? "bg-primary-50" : "")
            }
            onClick={() => onSelect(b)}
          >
            <div className="flex items-center justify-between">
              <code className="text-xs font-semibold">{b.scu_name}</code>
              <StateBadge state={b.state} />
            </div>
            <div className="text-xs text-secondary-500 mt-1">
              {b.smartbox_code ? `${b.smartbox_code} · ` : ""}
              {fmtCustomerWc(b.customer, b.work_center)}
              {operatorView ? ` · ${b.classification ?? "?"}` : ""}
            </div>
            {operatorView && (
              <div className="flex gap-2 mt-1">
                <SignalDot label="Pep" {...dots.pep} />
                <SignalDot label="Plug" {...dots.plug} />
                <SignalDot label="UPS" {...dots.ups} />
                <SignalDot label="TS" {...dots.ts} />
              </div>
            )}
            <div className="text-xs text-secondary-700 mt-1">
              Open for <strong>{fmtDuration(b.elapsed_seconds)}</strong>
              {operatorView && b.open_ticket_id && (
                <>
                  {" · "}
                  <span className="text-primary-700">
                    {b.open_ticket_origin} #{b.open_ticket_id}
                  </span>
                </>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default MapPage;
